/**
 * OrgHierarchyTree
 *
 * Interactive org node tree with expand/collapse, node type badges,
 * and CRUD action buttons (add child, edit, delete).
 *
 * Props:
 *   nodes       — flat array of org nodes (parent_id links)
 *   onAdd       — (parentNode) => void  — called when "Add child" is clicked
 *   onEdit      — (node) => void        — called when "Edit" is clicked
 *   onDelete    — (node) => void        — called when "Delete" is clicked
 *   readOnly    — boolean (default false)
 *   highlightId — id of the node to highlight (e.g. newly created)
 *
 * Phase 0 — Multi-Site Foundation
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import {
  NODE_TYPE_LABELS,
  NODE_TYPE_COLORS,
  NODE_TYPE_CHILDREN,
  getNodeDisplayLabel,
} from '../context/OrgContext';
import { MODULES } from '../utils/moduleConstants';
import { INDUSTRIES } from '../utils/industryConstants';
import { COUNTRIES } from '../utils/countries';
import { INDIAN_STATES } from '../constants/indianStates';
import { STATES_BY_COUNTRY, STATE_LABEL_BY_COUNTRY, findCountry } from '../utils/regional';

// BUG-001: IANA timezone list — built once at module load
const IANA_TIMEZONES = (() => {
  try {
    return Intl.supportedValuesOf('timeZone').sort();
  } catch {
    // Fallback for environments that don't support Intl.supportedValuesOf
    return ['Africa/Abidjan','America/New_York','America/Los_Angeles','America/Chicago',
      'Asia/Kolkata','Asia/Kuala_Lumpur','Asia/Singapore','Asia/Tokyo','Asia/Shanghai','Asia/Dubai',
      'Australia/Sydney','Europe/London','Europe/Paris','Europe/Berlin','UTC'];
  }
})();

// BUG-256: Country → IANA timezones so the Site timezone dropdown is scoped to
// the organisation's country. Keys are normalized (lowercase) and cover common
// aliases (IN, India, IND…). Countries not in this map fall back to the full
// IANA list so the form never becomes unusable.
const COUNTRY_TIMEZONES = {
  india: ['Asia/Kolkata'], in: ['Asia/Kolkata'], ind: ['Asia/Kolkata'],
  usa: ['America/New_York','America/Chicago','America/Denver','America/Los_Angeles','America/Phoenix','America/Anchorage','Pacific/Honolulu'],
  'united states': ['America/New_York','America/Chicago','America/Denver','America/Los_Angeles','America/Phoenix','America/Anchorage','Pacific/Honolulu'],
  us: ['America/New_York','America/Chicago','America/Denver','America/Los_Angeles','America/Phoenix','America/Anchorage','Pacific/Honolulu'],
  uk: ['Europe/London'], 'united kingdom': ['Europe/London'], gb: ['Europe/London'], britain: ['Europe/London'],
  germany: ['Europe/Berlin'], de: ['Europe/Berlin'],
  france: ['Europe/Paris'], fr: ['Europe/Paris'],
  japan: ['Asia/Tokyo'], jp: ['Asia/Tokyo'],
  china: ['Asia/Shanghai'], cn: ['Asia/Shanghai'],
  uae: ['Asia/Dubai'], 'united arab emirates': ['Asia/Dubai'], ae: ['Asia/Dubai'],
  australia: ['Australia/Sydney','Australia/Melbourne','Australia/Perth','Australia/Brisbane','Australia/Adelaide','Australia/Darwin','Australia/Hobart'],
  au: ['Australia/Sydney','Australia/Melbourne','Australia/Perth','Australia/Brisbane','Australia/Adelaide','Australia/Darwin','Australia/Hobart'],
  singapore: ['Asia/Singapore'], sg: ['Asia/Singapore'],
  // REG-03: Malaysia (single zone nationwide, incl. Sabah/Sarawak)
  malaysia: ['Asia/Kuala_Lumpur'], my: ['Asia/Kuala_Lumpur'], mys: ['Asia/Kuala_Lumpur'],
  canada: ['America/Toronto','America/Vancouver','America/Edmonton','America/Winnipeg','America/Halifax','America/St_Johns'],
  ca: ['America/Toronto','America/Vancouver','America/Edmonton','America/Winnipeg','America/Halifax','America/St_Johns'],
  brazil: ['America/Sao_Paulo','America/Manaus','America/Fortaleza','America/Recife','America/Bahia'],
  br: ['America/Sao_Paulo','America/Manaus','America/Fortaleza','America/Recife','America/Bahia'],
  'south africa': ['Africa/Johannesburg'], za: ['Africa/Johannesburg'],
  italy: ['Europe/Rome'], it: ['Europe/Rome'],
  spain: ['Europe/Madrid','Atlantic/Canary'], es: ['Europe/Madrid','Atlantic/Canary'],
  netherlands: ['Europe/Amsterdam'], nl: ['Europe/Amsterdam'],
  'saudi arabia': ['Asia/Riyadh'], sa: ['Asia/Riyadh'],
  'new zealand': ['Pacific/Auckland','Pacific/Chatham'], nz: ['Pacific/Auckland','Pacific/Chatham'],
  mexico: ['America/Mexico_City','America/Tijuana','America/Cancun','America/Monterrey','America/Chihuahua'],
  mx: ['America/Mexico_City','America/Tijuana','America/Cancun','America/Monterrey','America/Chihuahua'],
};

function resolveTimezonesForCountry(country) {
  if (!country) return null;
  const key = String(country).trim().toLowerCase();
  return COUNTRY_TIMEZONES[key] || null;
}

// BUG-256 (R2) / REG-09: canonical country list for the Site form dropdown.
// Built from the full ISO short-name list (utils/countries.js) so no country
// (e.g. Malaysia) falls into "Other". Storage format is unchanged: the
// title-case country name. Countries without a COUNTRY_TIMEZONES entry fall
// back to the full IANA list. Legacy stored values that are not in COUNTRIES
// (e.g. "Usa", "Uae", "Britain" from the old key-derived list) are kept
// selectable by the form itself — see legacyCountryOption below.
const COUNTRY_OPTIONS = COUNTRIES.map((name) => ({ value: name, label: name }));

// REG-03: map a free-form country value (name / ISO-2 / alias) to an ISO-2
// code for state-list selection. India keeps its historical aliases.
function countryCodeOf(country) {
  const v = String(country || '').trim().toLowerCase();
  if (!v) return '';
  if (v === 'india' || v === 'in' || v === 'ind') return 'IN';
  if (v === 'mys') return 'MY';
  const c = findCountry(v);
  return c ? c.code : '';
}

function findAncestorCountry(allNodes, startNode) {
  if (!allNodes || !allNodes.length || !startNode) return '';
  const byId = new Map(allNodes.map((n) => [n.id, n]));
  let current = startNode;
  let depth = 0;
  while (current && depth < 32) {
    const md = current.metadata || current.node_metadata || {};
    if (md.hq_country) return md.hq_country;
    if (!current.parent_id) return '';
    current = byId.get(current.parent_id);
    depth += 1;
  }
  return '';
}

// Nearest ancestor node of type 'country' — its NAME is the country (this is
// also how the backend resolves a site's country for statutory forms).
function findAncestorCountryNodeName(allNodes, startNode) {
  if (!allNodes || !allNodes.length || !startNode) return '';
  const byId = new Map(allNodes.map((n) => [n.id, n]));
  let current = startNode;
  let depth = 0;
  while (current && depth < 32) {
    if (current.node_type === 'country') return current.name || '';
    if (!current.parent_id) return '';
    current = byId.get(current.parent_id);
    depth += 1;
  }
  return '';
}

// ── Icons (inline SVG to avoid extra deps) ───────────────────────────────────

function ChevronIcon({ open }) {
  return (
    <svg
      className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`}
      fill="none" stroke="currentColor" viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}

function BuildingIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  );
}

// ── Build nested tree from flat list ────────────────────────────────────────

function buildTree(flatNodes) {
  const map = {};
  const roots = [];
  flatNodes.forEach((n) => { map[n.id] = { ...n, children: [] }; });
  flatNodes.forEach((n) => {
    if (n.parent_id && map[n.parent_id]) {
      map[n.parent_id].children.push(map[n.id]);
    } else {
      roots.push(map[n.id]);
    }
  });
  return roots;
}

// ── Single node row ──────────────────────────────────────────────────────────

function DocIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function MoveIcon() {
  return (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
    </svg>
  );
}

function NodeRow({ node, depth, expanded, onToggle, onAdd, onEdit, onDelete, onMove, onStatutoryForms, readOnly, highlightId }) {
  const hasChildren = node.children && node.children.length > 0;
  const allowedChildTypes = NODE_TYPE_CHILDREN[node.node_type] ?? [];
  const canAddChild = !readOnly && allowedChildTypes.length > 0;
  const isHighlighted = node.id === highlightId;

  return (
    <div>
      <div
        className={`flex items-center gap-1.5 py-1.5 px-2 rounded-md group hover:bg-gray-50 ${
          isHighlighted ? 'bg-yellow-50 ring-1 ring-yellow-300' : ''
        }`}
        style={{ paddingLeft: `${depth * 20 + 8}px` }}
      >
        {/* Expand / collapse toggle */}
        <button
          onClick={() => onToggle(node.id)}
          className={`flex-shrink-0 ${hasChildren ? 'cursor-pointer' : 'invisible'}`}
        >
          <ChevronIcon open={expanded} />
        </button>

        {/* Node type icon */}
        <BuildingIcon />

        {/* Name */}
        <span className="font-medium text-gray-800 text-sm flex-1 truncate">
          {node.name}
          {node.code && (
            <span className="ml-1.5 text-xs text-gray-400 font-mono">{node.code}</span>
          )}
          {node.metadata?.is_hq && (
            <span className="ml-1.5 text-xs bg-amber-100 text-amber-700 px-1 rounded">HQ</span>
          )}
        </span>

        {/* Node type badge */}
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${NODE_TYPE_COLORS[node.node_type] ?? 'bg-gray-100 text-gray-600'}`}>
          {getNodeDisplayLabel(node)}
        </span>

        {/* Action buttons (visible on hover) */}
        {!readOnly && (
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
            {canAddChild && (
              <button
                onClick={() => onAdd(node)}
                title="Add child node"
                className="p-1 rounded text-green-600 hover:bg-green-50"
              >
                <PlusIcon />
              </button>
            )}
            {node.node_type !== 'organization' && onMove && (
              <button
                onClick={() => onMove(node)}
                title="Move to different parent"
                className="p-1 rounded text-purple-600 hover:bg-purple-50"
              >
                <MoveIcon />
              </button>
            )}
            {node.node_type === 'country' && onStatutoryForms && (
              <button
                onClick={() => onStatutoryForms(node)}
                title="Statutory report forms for this jurisdiction"
                className="p-1 rounded text-cyan-700 hover:bg-cyan-50"
              >
                <DocIcon />
              </button>
            )}
            <button
              onClick={() => onEdit(node)}
              title="Edit node"
              className="p-1 rounded text-blue-600 hover:bg-blue-50"
            >
              <PencilIcon />
            </button>
            {/* Delete shown when parent passes onDelete (admins); type-to-confirm in modal */}
            {onDelete && (
              <button
                onClick={() => onDelete(node)}
                title="Delete node"
                className="p-1 rounded text-red-500 hover:bg-red-50"
              >
                <TrashIcon />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Children (rendered when expanded) */}
      {hasChildren && expanded && (
        <div className="border-l border-gray-200 ml-5">
          {node.children.map((child) => (
            <ConnectedNodeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              onAdd={onAdd}
              onEdit={onEdit}
              onDelete={onDelete}
              onMove={onMove}
              onStatutoryForms={onStatutoryForms}
              readOnly={readOnly}
              highlightId={highlightId}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Wrapper that manages its own expanded state so each row is self-contained
function ConnectedNodeRow({ node, depth, onAdd, onEdit, onDelete, onMove, onStatutoryForms, readOnly, highlightId }) {
  const [expanded, setExpanded] = useState(depth < 2); // auto-expand first 2 levels

  const toggle = useCallback(() => setExpanded((v) => !v), []);

  return (
    <NodeRow
      node={node}
      depth={depth}
      expanded={expanded}
      onToggle={toggle}
      onAdd={onAdd}
      onEdit={onEdit}
      onDelete={onDelete}
      onMove={onMove}
      onStatutoryForms={onStatutoryForms}
      readOnly={readOnly}
      highlightId={highlightId}
    />
  );
}

// ── Public component ─────────────────────────────────────────────────────────

export default function OrgHierarchyTree({
  nodes = [],
  onAdd,
  onEdit,
  onDelete,
  onMove,
  onStatutoryForms,
  readOnly = false,
  highlightId = null,
}) {
  const tree = buildTree(nodes);

  if (nodes.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400">
        <BuildingIcon />
        <p className="mt-2 text-sm">No organization structure defined yet.</p>
        {!readOnly && (
          <p className="text-xs mt-1">Use the setup wizard or add a root organization node to get started.</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-0.5">
      {tree.map((root) => (
        <ConnectedNodeRow
          key={root.id}
          node={root}
          depth={0}
          onAdd={onAdd ?? (() => {})}
          onEdit={onEdit ?? (() => {})}
          onDelete={onDelete}
          onMove={onMove}
          onStatutoryForms={onStatutoryForms}
          readOnly={readOnly}
          highlightId={highlightId}
        />
      ))}
    </div>
  );
}

// ── Node Form Modal (used by OrgHierarchyPage) ───────────────────────────────

export function NodeFormModal({ node, parentNode, onClose, onSave, saving, error, systemRoles = [], allNodes = [] }) {
  const isEdit = !!node;
  // On edit the type is immutable (select is disabled below), so the only
  // option must be the node's own type — callers pass parentNode=null when
  // editing, which would otherwise fall back to ['organization'] and make
  // the disabled select display the wrong type.
  const allowedTypes = isEdit
    ? [node.node_type]
    : parentNode
      ? (NODE_TYPE_CHILDREN[parentNode.node_type] ?? [])
      : ['organization'];

  // Preserve ALL existing metadata — form fields are merged on top
  const existingMeta = node?.metadata ?? {};

  // BUG-256 (R2): pre-fill country from existing meta, else from nearest
  // ancestor org's hq_country so new Sites inherit a sensible default.
  const _ancestorCountry = findAncestorCountry(allNodes, node || parentNode);
  const [formData, setFormData] = useState({
    name: node?.name ?? '',
    code: node?.code ?? '',
    node_type: node?.node_type ?? allowedTypes[0] ?? 'site',
    custom_label: node?.custom_label ?? '',
    metadata: {
      timezone: existingMeta.timezone ?? '',
      currency: existingMeta.currency ?? '',
      address: existingMeta.address ?? '',
      is_hq: existingMeta.is_hq ?? false,
      regulatory_tags: existingMeta.regulatory_tags ?? '',
      industry: existingMeta.industry ?? '',
      hq_country: existingMeta.hq_country ?? '',
      country: existingMeta.country ?? _ancestorCountry ?? '',
      state: existingMeta.state ?? '',
      // India Factories Rules statutory report particulars (prefill the
      // statutory accident form export modal, e.g. MP Form 21; editable there)
      factory_license_no: existingMeta.factory_license_no ?? '',
      factory_registration_no: existingMeta.factory_registration_no ?? '',
      esic_employer_code: existingMeta.esic_employer_code ?? '',
      esic_local_office: existingMeta.esic_local_office ?? '',
      occupier_name: existingMeta.occupier_name ?? '',
      occupier_address: existingMeta.occupier_address ?? '',
      occupier_phone: existingMeta.occupier_phone ?? '',
      nature_of_industry: existingMeta.nature_of_industry ?? '',
    },
    sort_order: node?.sort_order ?? 0,
  });

  // Module state for site nodes: Set of enabled module slugs
  const [enabledModules, setEnabledModules] = useState(() => {
    const mods = existingMeta.modules;
    if (Array.isArray(mods)) return new Set(mods);
    // Default: all modules enabled for new sites
    return new Set(MODULES.map((m) => m.slug));
  });

  // Role mappings state for site nodes
  const [roleMappings, setRoleMappings] = useState(() => {
    const rm = existingMeta.role_mappings;
    return Array.isArray(rm) ? rm.map((r) => ({ ...r })) : [];
  });
  // Client-side validation error (separate from the `error` prop, which
  // carries server-side failures returned by onSave).
  const [clientError, setClientError] = useState('');

  const isSiteType = formData.node_type === 'site' || formData.node_type === 'sub_site';
  const isOrgType = formData.node_type === 'organization';
  // For Country nodes the Name is picked from a fixed list (not free text) to
  // prevent typos — the value flows into statutory/regulatory reporting.
  const isCountryType = formData.node_type === 'country';

  // BUG-256 (R2): Resolve the applicable country for timezone filtering and
  // the State/UT dropdown. Priority: (1) this Site's own country, (2) org
  // node's hq_country, (3) ancestor org's hq_country, (4) the name of the
  // nearest 'country'-type ancestor node (matches backend jurisdiction logic).
  const resolvedCountry = useMemo(() => {
    if (formData.metadata.country) return formData.metadata.country;
    if (formData.metadata.hq_country) return formData.metadata.hq_country;
    const anchor = node || parentNode;
    return findAncestorCountry(allNodes, anchor) || findAncestorCountryNodeName(allNodes, anchor);
  }, [formData.metadata.country, formData.metadata.hq_country, node, parentNode, allNodes]);

  const timezoneOptions = useMemo(() => {
    return resolveTimezonesForCountry(resolvedCountry) || IANA_TIMEZONES;
  }, [resolvedCountry]);
  const timezoneFiltered = timezoneOptions !== IANA_TIMEZONES;

  const set = (key, value) => setFormData((prev) => ({ ...prev, [key]: value }));
  const setMeta = (key, value) =>
    setFormData((prev) => ({ ...prev, metadata: { ...prev.metadata, [key]: value } }));

  const toggleModule = (slug) => {
    setEnabledModules((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      return next;
    });
  };

  const toggleAllModules = () => {
    if (enabledModules.size === MODULES.length) {
      setEnabledModules(new Set());
    } else {
      setEnabledModules(new Set(MODULES.map((m) => m.slug)));
    }
  };

  const addRoleMapping = () => setRoleMappings((prev) => [...prev, { designation: '', systemRoleId: '' }]);
  const removeRoleMapping = (i) => setRoleMappings((prev) => prev.filter((_, idx) => idx !== i));
  const updateRoleMapping = (i, field, value) =>
    setRoleMappings((prev) => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r));

  // Separate system and custom roles for dropdown
  const sysRoles = systemRoles.filter((r) => r.is_system);
  const customRoles = systemRoles.filter((r) => !r.is_system);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Code is required. The `required` attribute does not reject whitespace-only
    // input, so validate the trimmed value here.
    const trimmedCode = (formData.code ?? '').trim();
    if (!trimmedCode) {
      setClientError('Code is required and cannot be blank.');
      return;
    }
    setClientError('');
    // Merge form metadata on top of existing metadata to preserve unknown fields
    const mergedMetadata = {
      ...existingMeta,
      ...formData.metadata,
    };
    // Add site-specific computed fields
    if (isSiteType) {
      mergedMetadata.modules = Array.from(enabledModules);
      mergedMetadata.role_mappings = roleMappings.filter((r) => r.designation && r.systemRoleId);
    }
    const payload = {
      ...formData,
      code: trimmedCode,
      metadata: mergedMetadata,
      parent_id: isEdit ? node.parent_id : (parentNode?.id ?? null),
    };
    // Only include custom_label for "other" type; clear it for standard types
    if (payload.node_type !== 'other') {
      delete payload.custom_label;
    }
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
        <h2 className="text-lg font-bold text-gray-900 mb-4">
          {isEdit ? `Edit: ${node.name}` : `Add ${parentNode ? 'Child to ' + parentNode.name : 'Root Node'}`}
        </h2>

        {(clientError || error) && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
            {clientError || error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Node Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Node Type</label>
            <select
              value={formData.node_type}
              onChange={(e) => set('node_type', e.target.value)}
              disabled={isEdit || allowedTypes.length === 1}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
            >
              {allowedTypes.map((t) => (
                <option key={t} value={t}>{NODE_TYPE_LABELS[t] ?? t}</option>
              ))}
            </select>
          </div>

          {/* Custom Label — shown only when node_type is "other" */}
          {formData.node_type === 'other' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Layer Label <span className="text-red-500">*</span></label>
              <input
                type="text"
                required
                value={formData.custom_label}
                onChange={(e) => set('custom_label', e.target.value)}
                placeholder="e.g. Division, Zone, Plant Group"
                maxLength={100}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-400 mt-0.5">This label replaces "Custom" in the hierarchy display</p>
            </div>
          )}

          {/* Name — Country nodes use a fixed dropdown (typo-safe; feeds
              statutory reporting); everything else is free text. */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            {isCountryType ? (
              <select
                required
                value={formData.name}
                onChange={(e) => set('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">— Select Country —</option>
                {COUNTRIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder={`e.g. Mumbai Plant`}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            )}
            <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder={`e.g. Mumbai Plant`}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Code <span className="text-red-500">*</span></label>
            <input
              type="text"
              required
              value={formData.code}
              onChange={(e) => set('code', e.target.value)}
              placeholder="e.g. MUM-01"
              maxLength={50}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-400 mt-0.5">Required — unique identifier within your tenant</p>
          </div>

          {/* Organization-specific: Industry */}
          {isOrgType && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
                <select
                  value={formData.metadata.industry}
                  onChange={(e) => setMeta('industry', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select industry…</option>
                  {INDUSTRIES.map((ind) => (
                    <option key={ind.value} value={ind.value}>{ind.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">HQ Country</label>
                <input
                  type="text"
                  value={formData.metadata.hq_country}
                  onChange={(e) => setMeta('hq_country', e.target.value)}
                  placeholder="e.g. India"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          {/* Site-specific fields */}
          {isSiteType && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <textarea
                  value={formData.metadata.address}
                  onChange={(e) => setMeta('address', e.target.value)}
                  rows={2}
                  placeholder="Full site address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              {/* BUG-256 (R2): Country dropdown drives the Timezone filter
                  directly. Pre-filled from the nearest ancestor org's
                  hq_country so new sites inherit it, but user can override. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <select
                  value={formData.metadata.country}
                  onChange={(e) => setMeta('country', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">— Select Country —</option>
                  {/* Legacy stored value not in the canonical list (e.g. "Usa")
                      — keep it selectable so editing never silently drops it. */}
                  {formData.metadata.country
                    && formData.metadata.country !== 'Other'
                    && !COUNTRIES.includes(formData.metadata.country) && (
                    <option value={formData.metadata.country}>{formData.metadata.country}</option>
                  )}
                  {COUNTRY_OPTIONS.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                  <option value="Other">Other</option>
                </select>
                <p className="text-xs text-gray-400 mt-0.5">
                  Drives the Timezone list below. Leave blank to use the full IANA list.
                </p>
              </div>
              {/* State/UT drives the state-specific statutory accident form
                  (each Indian state's Factories Rules prescribe the form number).
                  REG-03: generic per country — India: INDIAN_STATES; Malaysia:
                  STATES_BY_COUNTRY.MY; any other country: free text. All write
                  the same metadata.state key. */}
              {(() => {
                if (!String(resolvedCountry || '').trim()) return null;
                const code = countryCodeOf(resolvedCountry);
                const stateList = code === 'IN' ? INDIAN_STATES : (STATES_BY_COUNTRY[code] || null);
                const label = STATE_LABEL_BY_COUNTRY[code] || 'State / Province / Region';
                const current = formData.metadata.state || '';
                return (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                    {stateList ? (
                      <select
                        value={current}
                        onChange={(e) => setMeta('state', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">— Select {label} —</option>
                        {current && !stateList.includes(current) && (
                          <option value={current}>{current}</option>
                        )}
                        {stateList.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={current}
                        onChange={(e) => setMeta('state', e.target.value)}
                        maxLength={100}
                        placeholder="State / Province / Region"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    )}
                    {code === 'IN' && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        Determines the state-specific statutory accident form (state Factories Rules).
                      </p>
                    )}
                  </div>
                );
              })()}
              {/* Statutory report particulars (India Factories Rules) — prefill
                  the accident form export modal (e.g. MP Form 21), editable
                  per export. Stored in org_nodes.metadata. */}
              {String(resolvedCountry || '').trim().toLowerCase() === 'india' && (
                <div className="border border-gray-200 rounded-md p-3 space-y-3">
                  <p className="text-sm font-medium text-gray-700">Statutory report details (India Factories Rules)</p>
                  <p className="text-xs text-gray-400 -mt-2">
                    Prefills the statutory accident report (e.g. MP Form 21); editable per export.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Factory License No.</label>
                      <input
                        type="text"
                        value={formData.metadata.factory_license_no}
                        onChange={(e) => setMeta('factory_license_no', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Registration No.</label>
                      <input
                        type="text"
                        value={formData.metadata.factory_registration_no}
                        onChange={(e) => setMeta('factory_registration_no', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">ESIC Employer's Code</label>
                      <input
                        type="text"
                        value={formData.metadata.esic_employer_code}
                        onChange={(e) => setMeta('esic_employer_code', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nature of Industry (as per license)</label>
                      <input
                        type="text"
                        value={formData.metadata.nature_of_industry}
                        onChange={(e) => setMeta('nature_of_industry', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Local ESIC Office (name and address)</label>
                    <textarea
                      value={formData.metadata.esic_local_office}
                      onChange={(e) => setMeta('esic_local_office', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Occupier Name</label>
                      <input
                        type="text"
                        value={formData.metadata.occupier_name}
                        onChange={(e) => setMeta('occupier_name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Occupier Phone</label>
                      <input
                        type="text"
                        value={formData.metadata.occupier_phone}
                        onChange={(e) => setMeta('occupier_phone', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Occupier Address</label>
                    <textarea
                      value={formData.metadata.occupier_address}
                      onChange={(e) => setMeta('occupier_address', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_hq"
                  checked={formData.metadata.is_hq}
                  onChange={(e) => setMeta('is_hq', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600"
                />
                <label htmlFor="is_hq" className="text-sm text-gray-700">This is the HQ site</label>
              </div>
            </>
          )}

          {/* Timezone & Currency */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
              {/* BUG-001: IANA dropdown replaces free-text input.
                  BUG-256: list is filtered by the resolved country when known. */}
              <select
                value={formData.metadata.timezone}
                onChange={(e) => setMeta('timezone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">— Select Timezone —</option>
                {timezoneOptions.map((tz) => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
              {timezoneFiltered && (
                <p className="text-xs text-gray-400 mt-0.5">
                  Filtered by country: {resolvedCountry}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
              <input
                type="text"
                value={formData.metadata.currency}
                onChange={(e) => setMeta('currency', e.target.value)}
                placeholder="INR"
                maxLength={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Regulatory Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Regulatory Tags</label>
            <input
              type="text"
              value={formData.metadata.regulatory_tags}
              onChange={(e) => setMeta('regulatory_tags', e.target.value)}
              placeholder="ISO 45001, OSHA, REACH …"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* ── Modules (site/sub_site only) ────────────────────────────── */}
          {isSiteType && (
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">Enabled Modules</label>
                <button
                  type="button"
                  onClick={toggleAllModules}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  {enabledModules.size === MODULES.length ? 'Disable all' : 'Enable all'}
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {MODULES.map((mod) => (
                  <label key={mod.slug} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={enabledModules.has(mod.slug)}
                      onChange={() => toggleModule(mod.slug)}
                      className="rounded border-gray-300 text-blue-600"
                    />
                    <span className="text-sm text-gray-700">
                      <span className="mr-1">{mod.icon}</span>{mod.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* ── Role Mappings (site/sub_site only) ──────────────────────── */}
          {isSiteType && (
            <div className="border-t pt-4 mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Role Mappings</label>
              {roleMappings.length > 0 && (
                <div className="space-y-2 mb-2">
                  <div className="grid grid-cols-[1fr_1fr_auto] gap-2 px-1">
                    <span className="text-xs font-medium text-gray-500 uppercase">Site Designation</span>
                    <span className="text-xs font-medium text-gray-500 uppercase">System Role</span>
                    <span />
                  </div>
                  {roleMappings.map((mapping, i) => (
                    <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                      <input
                        type="text"
                        value={mapping.designation}
                        onChange={(e) => updateRoleMapping(i, 'designation', e.target.value)}
                        placeholder="e.g. Plant Manager"
                        className="px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <select
                        value={mapping.systemRoleId}
                        onChange={(e) => updateRoleMapping(i, 'systemRoleId', e.target.value)}
                        className="px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select role…</option>
                        {sysRoles.length > 0 && (
                          <optgroup label="System Roles">
                            {sysRoles.map((r) => (
                              <option key={r.id} value={r.id}>{r.display_name || r.name}</option>
                            ))}
                          </optgroup>
                        )}
                        {customRoles.length > 0 && (
                          <optgroup label="Custom Roles">
                            {customRoles.map((r) => (
                              <option key={r.id} value={r.id}>{r.display_name || r.name}</option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                      <button
                        type="button"
                        onClick={() => removeRoleMapping(i)}
                        className="text-red-400 hover:text-red-600 text-lg leading-none px-1"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={addRoleMapping}
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
              >
                <span className="text-lg leading-none">+</span> Add Role Mapping
              </button>
            </div>
          )}

          <div className="flex gap-2 justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:bg-blue-300"
            >
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Node'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Move Node Modal ───────────────────────────────────────────────────────────

export function MoveNodeModal({ node, allNodes, onClose, onSave, saving, error }) {
  const [newParentId, setNewParentId] = useState(node?.parent_id ?? '');

  // Build list of valid parents: all nodes except the node itself and its descendants
  const getDescendantIds = (nodeId, nodes) => {
    const ids = new Set();
    const collect = (pid) => {
      nodes.filter((n) => n.parent_id === pid).forEach((n) => {
        ids.add(n.id);
        collect(n.id);
      });
    };
    collect(nodeId);
    return ids;
  };

  const descendantIds = getDescendantIds(node.id, allNodes);
  const validParents = allNodes.filter(
    (n) => n.id !== node.id && !descendantIds.has(n.id) && n.node_type !== 'production_line'
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (newParentId && newParentId !== node.parent_id) {
      onSave(newParentId);
    }
  };

  const selectedParent = allNodes.find((n) => n.id === newParentId);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
        <h2 className="text-lg font-bold text-gray-900 mb-1">Move "{node.name}"</h2>
        <p className="text-sm text-gray-500 mb-4">Select a new parent node.</p>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Parent</label>
            <select
              value={newParentId}
              onChange={(e) => setNewParentId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select parent…</option>
              {validParents.map((n) => (
                <option key={n.id} value={n.id}>
                  {'  '.repeat(n.level ?? 0)}{n.name} ({getNodeDisplayLabel(n)})
                </option>
              ))}
            </select>
          </div>

          {selectedParent && selectedParent.id !== node.parent_id && (
            <p className="text-sm text-gray-600">
              Moving <strong>{node.name}</strong> under <strong>{selectedParent.name}</strong>
            </p>
          )}

          <div className="flex gap-2 justify-end pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving || !newParentId || newParentId === node.parent_id}
              className="px-4 py-2 bg-purple-600 text-white rounded-md text-sm hover:bg-purple-700 disabled:bg-purple-300">
              {saving ? 'Moving…' : 'Move Node'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Delete Node Modal (type-to-confirm) ──────────────────────────────────────
// Destructive action: requires the admin to type the node's exact name before
// the soft-delete is enabled, guarding against accidental deletion.
//
// `impact` is the /deletion-impact payload ({ counts, assigned_users,
// total_records, subtree_node_count }) or null while loading. The soft-delete
// does NOT remove these records — they keep their site_id and become orphaned /
// hidden — so we surface the counts to make that consequence explicit.
const IMPACT_LABELS = {
  incidents: 'Incidents',
  hazards: 'Hazards',
  risk_assessments: 'Risk Assessments (HIRA)',
  capas: 'CAPA',
  safety_meetings: 'Safety Meetings',
  permits_to_work: 'Permits to Work',
  ssem_assessments: 'SSEM Assessments',
  training_courses: 'Training Courses',
  training_records: 'Training Records',
};

export function DeleteNodeModal({ node, hasChildren, impact, impactLoading, onClose, onConfirm, deleting, error }) {
  const [typed, setTyped] = useState('');
  const confirmed = typed.trim() === node.name;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (confirmed && !deleting) onConfirm();
  };

  const counts = impact?.counts ?? {};
  const attached = Object.entries(IMPACT_LABELS)
    .map(([key, label]) => ({ label, count: counts[key] ?? 0 }))
    .filter((row) => row.count > 0);
  const hasImpact = attached.length > 0 || (impact?.assigned_users ?? 0) > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
        <h2 className="text-lg font-bold text-gray-900 mb-1">Delete "{node.name}"</h2>
        <p className="text-sm text-gray-500 mb-4">
          This soft-deletes the node{hasChildren ? ' and all of its child nodes' : ''}. It
          will no longer appear in your hierarchy. This action cannot be undone from the app.
        </p>

        {/* Data-impact warning so the admin sees what gets orphaned. */}
        {impactLoading ? (
          <div className="mb-4 text-sm text-gray-400">Checking attached records…</div>
        ) : hasImpact ? (
          <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded text-sm">
            <p className="font-semibold mb-1">
              This will orphan existing records linked to {impact?.subtree_node_count > 1 ? 'these sites' : 'this site'}:
            </p>
            <ul className="list-disc list-inside space-y-0.5">
              {attached.map((row) => (
                <li key={row.label}>{row.count.toLocaleString()} {row.label}</li>
              ))}
              {(impact?.assigned_users ?? 0) > 0 && (
                <li>{impact.assigned_users.toLocaleString()} user role assignment(s)</li>
              )}
            </ul>
            <p className="mt-2 text-xs text-amber-700">
              These records are retained in the database but will no longer be reachable
              through the site. Consider reassigning or closing them first.
            </p>
          </div>
        ) : impact ? (
          <div className="mb-4 text-sm text-gray-500">No operational records are linked to this node.</div>
        ) : null}

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type <span className="font-semibold text-gray-900">{node.name}</span> to confirm
            </label>
            <input
              type="text"
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={node.name}
              autoComplete="off"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          <div className="flex gap-2 justify-end pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={!confirmed || deleting}
              className="px-4 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700 disabled:bg-red-300 disabled:cursor-not-allowed">
              {deleting ? 'Deleting…' : 'Delete Node'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Statutory Forms Modal (per country/state node) ───────────────────────────
//
// Admin editor for the statutory report forms of a jurisdiction node.
// Shows the effective config (built-in defaults or a node-level override) and
// lets admins customize form number/title/citation per state — e.g. Maharashtra
// prescribes Form 24 under its Factories Rules where other states use Form 18.
// Backed by GET/PUT/DELETE /org/nodes/{id}/statutory-forms-config.

// Mirrors backend utils/statutory_form_defaults.py RENDERER_KEYS / MODAL_TYPES.
const STATUTORY_RENDERER_OPTIONS = [
  { value: 'india_accident_notice', label: 'India Accident Notice (Factories Act PDF)' },
  { value: 'india_form21_mp', label: 'India Form 21 — Madhya Pradesh (Rule 103 layout)' },
  { value: 'india_form24_mh', label: 'India Form 24 — Maharashtra (Rule 115 layout)' },
  { value: 'sg_mom_ireport', label: 'Singapore MOM iReport PDF' },
  { value: 'my_jkkp6', label: 'Malaysia JKKP 6 PDF' },
  { value: 'my_jkkp7', label: 'Malaysia JKKP 7 PDF' },
];
const STATUTORY_MODAL_TYPES = ['india', 'india_form21', 'india_form24', 'singapore', 'malaysia'];

const EMPTY_STATUTORY_ENTRY = {
  form_family: 'india_accident_notice',
  state: '',
  form_code: '',
  title: '',
  citation: '',
  display_label: '',
  group_label: '',
  filename_prefix: '',
  renderer_key: 'india_accident_notice',
  modal_type: 'india',
  sort_order: 0,
  verified: false,
};

export function StatutoryFormsModal({ node, onClose }) {
  const [source, setSource] = useState(null); // 'node_config' | 'default'
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API}/org/nodes/${node.id}/statutory-forms-config`,
        { headers: authHeaders() }
      );
      setSource(res.data?.source || 'default');
      setEntries((res.data?.forms || []).map((f) => ({ ...EMPTY_STATUTORY_ENTRY, ...f })));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load statutory forms config');
    } finally {
      setLoading(false);
    }
  }, [node.id]);

  useEffect(() => { load(); }, [load]);

  const setEntry = (index, key, value) => {
    setEntries((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [key]: value };
      return next;
    });
  };

  const addEntry = () => setEntries((prev) => [...prev, { ...EMPTY_STATUTORY_ENTRY }]);
  const removeEntry = (index) => setEntries((prev) => prev.filter((_, i) => i !== index));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const forms = entries.map((e) => ({
        ...e,
        group_label: e.group_label || null,
        state: (e.state || '').trim() || null,
        sort_order: Number(e.sort_order) || 0,
      }));
      const res = await axios.put(
        `${API}/org/nodes/${node.id}/statutory-forms-config`,
        { forms },
        { headers: authHeaders() }
      );
      setSource(res.data?.source || 'node_config');
      setEntries((res.data?.forms || []).map((f) => ({ ...EMPTY_STATUTORY_ENTRY, ...f })));
      onClose(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to save statutory forms config');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    setError(null);
    try {
      await axios.delete(
        `${API}/org/nodes/${node.id}/statutory-forms-config`,
        { headers: authHeaders() }
      );
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset to defaults');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 overflow-y-auto">
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full my-8 p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                Statutory Report Forms — {node.name}
              </h2>
              <p className="text-xs text-gray-500 mt-1">
                Applies to all sites under this country. An entry with a State applies only to
                sites whose State property matches; entries without a State are the country-wide default.
              </p>
              {source && (
                <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded-full font-medium ${
                  source === 'node_config' ? 'bg-cyan-100 text-cyan-800' : 'bg-gray-100 text-gray-600'
                }`}>
                  {source === 'node_config' ? 'Customized for this node' : 'Built-in defaults'}
                </span>
              )}
            </div>
            <button onClick={() => onClose(false)} className="text-gray-500 hover:text-gray-700 text-xl leading-none">×</button>
          </div>

          {error && (
            <div className="mb-3 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">{error}</div>
          )}

          {loading ? (
            <div className="py-10 text-center text-gray-400 text-sm">Loading…</div>
          ) : (
            <>
              <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-1">
                {entries.map((entry, i) => (
                  <div key={entry.id || i} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-semibold text-gray-700">
                        {entry.display_label || `Form ${i + 1}`}
                        {entry.verified === false && (
                          <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">unverified</span>
                        )}
                      </span>
                      {entries.length > 1 && (
                        <button onClick={() => removeEntry(i)} className="text-xs text-red-500 hover:text-red-700">Remove</button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <label className="block col-span-2">
                        <span className="text-xs text-gray-500">State (optional — blank = whole country)</span>
                        <input value={entry.state || ''} onChange={(e) => setEntry(i, 'state', e.target.value)}
                          placeholder="e.g. Maharashtra"
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-md" />
                      </label>
                      <label className="block">
                        <span className="text-xs text-gray-500">Form code *</span>
                        <input value={entry.form_code} onChange={(e) => setEntry(i, 'form_code', e.target.value)}
                          placeholder="e.g. Form 24"
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-md" />
                      </label>
                      <label className="block">
                        <span className="text-xs text-gray-500">Button/modal label *</span>
                        <input value={entry.display_label} onChange={(e) => setEntry(i, 'display_label', e.target.value)}
                          placeholder="e.g. India Form 24 (Maharashtra)"
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-md" />
                      </label>
                      <label className="block col-span-2">
                        <span className="text-xs text-gray-500">PDF title *</span>
                        <input value={entry.title} onChange={(e) => setEntry(i, 'title', e.target.value)}
                          placeholder="e.g. FORM 24 - NOTICE OF ACCIDENT"
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-md" />
                      </label>
                      <label className="block col-span-2">
                        <span className="text-xs text-gray-500">Legal citation *</span>
                        <input value={entry.citation} onChange={(e) => setEntry(i, 'citation', e.target.value)}
                          placeholder="e.g. (Under the Maharashtra Factories Rules, 1963)"
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-md" />
                      </label>
                      <label className="block">
                        <span className="text-xs text-gray-500">Filename prefix *</span>
                        <input value={entry.filename_prefix} onChange={(e) => setEntry(i, 'filename_prefix', e.target.value)}
                          placeholder="e.g. Form24_Accident_Report"
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-md" />
                      </label>
                      <label className="block">
                        <span className="text-xs text-gray-500">PDF renderer *</span>
                        <select value={entry.renderer_key} onChange={(e) => setEntry(i, 'renderer_key', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-md bg-white">
                          {STATUTORY_RENDERER_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </label>
                      <label className="block">
                        <span className="text-xs text-gray-500">Data-entry form type *</span>
                        <select value={entry.modal_type} onChange={(e) => setEntry(i, 'modal_type', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-md bg-white">
                          {STATUTORY_MODAL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </label>
                      <label className="flex items-center gap-2 mt-4">
                        <input type="checkbox" checked={!!entry.verified}
                          onChange={(e) => setEntry(i, 'verified', e.target.checked)} />
                        <span className="text-xs text-gray-600">Form number verified by compliance</span>
                      </label>
                    </div>
                  </div>
                ))}
              </div>

              <button onClick={addEntry} className="mt-3 text-sm text-cyan-700 hover:text-cyan-900 font-medium">
                + Add form
              </button>

              <div className="flex gap-2 justify-between pt-4 mt-2 border-t border-gray-100">
                <div>
                  {source === 'node_config' && (
                    <button onClick={handleReset} disabled={saving}
                      className="px-3 py-2 text-sm text-amber-700 border border-amber-300 rounded-md hover:bg-amber-50 disabled:opacity-50">
                      Reset to defaults
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => onClose(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
                  <button onClick={handleSave} disabled={saving || entries.length === 0}
                    className="px-4 py-2 bg-[#1B5C94] text-white rounded-md text-sm hover:bg-[#164a77] disabled:opacity-50">
                    {saving ? 'Saving…' : 'Save Override'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
